import React, { useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp } from "react-feather";
import useLanguage from "../../hooks/language";
import { Button } from "../../components";
import "./consent.css";
import { ConsentModalView } from "./consent-flow";

export type { ConsentModalView } from "./consent-flow";

interface ConsentModalProps {
    view: ConsentModalView;
    onViewChange: (view: ConsentModalView) => void;
    onConsent: () => void;
    onExitScreening: () => void;
}

const ConsentModal: React.FunctionComponent<ConsentModalProps> = ({
    view,
    onViewChange,
    onConsent,
    onExitScreening,
}: ConsentModalProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const { translations } = useLanguage();
    const t = translations.questionnaireSection.modal;

    useLayoutEffect(() => {
        const scrollContainer = contentRef.current?.parentElement;
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
    }, [view]);

    if (view === "exit") {
        return (
            <div ref={contentRef}>
                <p className="consent-modal__body-text">{t.refusalExitBody}</p>
                <div className="consent-modal__actions">
                    <Button onClick={onExitScreening}>
                        {t.refusalExitConfirmButtonText}
                    </Button>
                    <Button
                        onClick={() => onViewChange("refuse")}
                        type="secondary"
                    >
                        {t.refusalExitCancelButtonText}
                    </Button>
                </div>
            </div>
        );
    }

    if (view === "refuse") {
        return (
            <div ref={contentRef}>
                <RefuseView
                    onBack={() => onViewChange("consent")}
                    onExit={() => onViewChange("exit")}
                />
            </div>
        );
    }

    return (
        <div ref={contentRef}>
            <p>{t.consentFirstP}</p>
            <p>{t.consentSecondP}</p>
            <p>{t.consentAcknowledgementHeader}</p>
            <ul>
                {t.consentAcknowledgements.map((acknowledgement, index) => (
                    <li key={index}>{acknowledgement}</li>
                ))}
                <li>
                    {t.consentPrivacyPolicyAcknowledgementPrefix}
                    <a
                        className="consent-modal__privacy-policy-link"
                        href={translations.footer.privacyPolicyLink}
                    >
                        {translations.footer.privacyPolicy}
                    </a>
                    .
                </li>
            </ul>
            <p>{t.consentThirdP}</p>
            <p>{t.consentFourthP}</p>
            <div className="consent-modal__actions">
                <Button onClick={onConsent}>{t.consentButtonText}</Button>
                <Button onClick={() => onViewChange("refuse")} type="secondary">
                    {t.refuseButtonText}
                </Button>
            </div>
        </div>
    );
};

interface RefuseViewProps {
    onBack: () => void;
    onExit: () => void;
}

const RefuseView: React.FunctionComponent<RefuseViewProps> = ({
    onBack,
    onExit,
}: RefuseViewProps) => {
    const { translations } = useLanguage();
    const t = translations.questionnaireSection.modal;
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

    return (
        <div>
            <h3 className="consent-modal__refusal-title">{t.refusalTitle}</h3>
            <p className="consent-modal__body-text">{t.refusalBody}</p>

            <div className="consent-modal__section-label">
                {t.refusalWhatHeading}
            </div>
            <ul className="consent-modal__refusal-list">
                {t.refusalItems.map((item, index) => (
                    <li
                        key={index}
                        className="consent-modal__refusal-list-item"
                    >
                        <span className="consent-modal__refusal-list-marker">
                            —
                        </span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>

            <div className="consent-modal__faq">
                <div className="consent-modal__section-label">
                    {t.refusalFaqHeading}
                </div>
                {t.refusalFaq.map((faqItem, index) => (
                    <div key={index} className="consent-modal__faq-item">
                        <button
                            type="button"
                            className="consent-modal__faq-question"
                            onClick={() =>
                                setOpenFaqIndex(
                                    openFaqIndex === index ? null : index
                                )
                            }
                            aria-expanded={openFaqIndex === index}
                        >
                            <span>{faqItem.question}</span>
                            {openFaqIndex === index ? (
                                <ChevronUp size={16} />
                            ) : (
                                <ChevronDown size={16} />
                            )}
                        </button>
                        {openFaqIndex === index && (
                            <p className="consent-modal__faq-answer">
                                {faqItem.answer}
                            </p>
                        )}
                    </div>
                ))}
            </div>

            <div className="consent-modal__actions">
                <Button onClick={onBack}>{t.refusalBackButtonText}</Button>
                <Button onClick={onExit} type="secondary">
                    {t.refusalExitButtonText}
                </Button>
                <a
                    className="consent-modal__help-link"
                    href="mailto:info@prelib.com"
                >
                    {t.refusalHelpLinkText}
                </a>
            </div>
        </div>
    );
};

export default ConsentModal;
